import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';  // âœ“ signOut imported
import { useNavigate } from 'react-router-dom';
import MapView from '../components/MapView';
import JourneyModal from '../components/JourneyModal';
import VisitModal from '../components/VisitModal';
import LoginModal from '../components/LoginModal';
import Footer from '../components/Footer';
import AboutModal from '../components/AboutModal';
import TermsModal from '../components/TermsModal';
import PrivacyModal from '../components/PrivacyModal';
import SubscribeModal from '../components/SubscribeModal';
import { api } from '../utils/api';
import './DiscPage.css';

// Helper function to format visit numbers (1st, 2nd, 3rd, etc.)
const formatVisitNumber = (num) => {
    const suffix = ['th', 'st', 'nd', 'rd'];
    const v = num % 100;
    return num + (suffix[(v - 20) % 10] || suffix[v] || suffix[0]);
};

// Helper to check if user is in family group
const checkUserGroups = async () => {
    try {
        const session = await fetchAuthSession();
        const groups = session.tokens?.accessToken?.payload['cognito:groups'] || [];
        return {
            isFamily: groups.includes('family'),
            isAdmin: groups.includes('admin'),
            isAuthenticated: !!session.tokens
        };
    } catch (err) {
        return {
            isFamily: false,
            isAdmin: false,
            isAuthenticated: false
        };
    }
};

const DiscPage = () => {
    const { discId } = useParams();
    const [disc, setDisc] = useState(null);
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isJourneyModalOpen, setIsJourneyModalOpen] = useState(false);
    const [userGroups, setUserGroups] = useState({ isFamily: false, isAdmin: false });
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
    const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
    const navigate = useNavigate();

    // Check if this visit is from a map click or QR code scan
    const urlParams = new URLSearchParams(window.location.search);
    const isFromMap = urlParams.get('source') === 'map';
    const isQRScan = !isFromMap; // QR scans get some content that non-QR don't

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const session = await fetchAuthSession();
                setIsLoggedIn(!!session.tokens);
            } catch {
                setIsLoggedIn(false);
            }
        };
        checkAuth();
    }, []);
    
    useEffect(() => {
        const loadUserGroups = async () => {
            try {
                const session = await fetchAuthSession();
                const groups = session.tokens?.accessToken?.payload['cognito:groups'] || [];
                setUserGroups({
                    isFamily: groups.includes('family'),
                    isAdmin: groups.includes('admin')
                });
            } catch (err) {
                setUserGroups({ isFamily: false, isAdmin: false });
            }
        };
        
        loadUserGroups();
    }, []);
    
    const handleMarkerClick = (visit) => {
        // Open journey modal when clicking a pin
        setIsJourneyModalOpen(true);
    };

    useEffect(() => {
        loadDisc();
    }, [discId]);

    const loadDisc = async () => {
        try {
            setLoading(true);
            const data = await api.getDisc(discId);
            setDisc(data.disc);
            setVisits(data.visits);
            setError(null);
        } catch (err) {
            console.error('Error loading disc:', err);
            setError('Failed to load disc information.');
        } finally {
            setLoading(false);
        }
    };

    const handleSignInClick = () => {
        setIsLoginModalOpen(true);
    };

    const handleLoginSuccess = () => {
        setIsLoginModalOpen(false);
        // Reload to refresh user groups and show family content
        window.location.reload();
    };

    const handleLogout = async () => {
        try {
            await signOut();
            setIsLoggedIn(false);
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

    if (loading) {
        return <div className="disc-page loading">Loading disc information...</div>;
    }

    if (error) {
        return (
            <div className="disc-page error">
                <p>{error}</p>
                <button onClick={loadDisc}>Try Again</button>
            </div>
        );
    }

    if (!disc) {
        return <div className="disc-page error">Disc not found</div>;
    }

    return (
        <div className="disc-page">
            <header className="disc-header">
                <div className="disc-title">Disc #{discId.replace('JD', '').replace(/^0+/, '')}: "{disc.nickname || 'Bittersweet'}"</div>
                {isQRScan && (
                    <p className="visit-count">You are the {formatVisitNumber(visits.length + 2)} person to find this disc</p>
                )}
                <p className="memorial-text">Please help us honor the memory of Jeffrey, who died too young and who loved disc golf</p>
            </header>

            <div className="disc-content">
                <div className="disc-actions">
                    {isQRScan && (
                        <button 
                            className="action-button primary"
                            disabled={isModalOpen}
                            onClick={() => setIsModalOpen(true)}
                        >
                            Update This Disc's Position & Record Your Thoughts
                        </button>
                    )}
                    <button 
                        className="action-button secondary"
                        onClick={() => setIsJourneyModalOpen(true)}
                    >
                        See This Disc's Journey Details
                    </button>
                </div>

                {visits.length === 0 ? (
                    <div className="no-visits">
                        <p>[Map Will Go Here]</p>
                    </div>
                ) : (
                    <div className="disc-map">
                        <h2>Disc Journey ({visits.length} {visits.length === 1 ? 'visit' : 'visits'})</h2>
                        <MapView 
                            visits={visits}
                            showJourney={true}
                            onMarkerClick={handleMarkerClick}
                        />
                    </div>
                )}

                {!loading && !error && disc && (
                    <div className="disc-actions">
                        <button 
                            className="see-all-button"
                            onClick={() => navigate('/')}
                        >
                            See All Discs
                        </button>
                        <button 
                            className="about-button"
                            onClick={() => setIsAboutModalOpen(true)}
                        >
                            About Jeffrey Discs
                        </button>
                        <button 
                            className="subscribe-button"
                            onClick={() => setIsSubscribeModalOpen(true)}
                        >
                           Get Monthly Disc Updates
                        </button>
                    </div>
                )}

            </div>

            <Footer 
                onAboutClick={() => setIsAboutModalOpen(true)}
                onTermsClick={() => setIsTermsModalOpen(true)}
                onPrivacyClick={() => setIsPrivacyModalOpen(true)}
                isLoggedIn={isLoggedIn}
                onLoginClick={() => setIsLoginModalOpen(true)}
                onLogoutClick={handleLogout}
            />

            <JourneyModal 
                isOpen={isJourneyModalOpen}
                onClose={() => setIsJourneyModalOpen(false)}
                visits={visits}
                onSignInClick={handleSignInClick}
            />

            <VisitModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                discId={discId}
            />

            <LoginModal 
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
                onLoginSuccess={handleLoginSuccess}
            />

            <AboutModal 
                isOpen={isAboutModalOpen}
                onClose={() => setIsAboutModalOpen(false)}
            />

            <TermsModal 
                isOpen={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
            />

            <PrivacyModal 
                isOpen={isPrivacyModalOpen}
                onClose={() => setIsPrivacyModalOpen(false)}
            />

            <SubscribeModal 
                isOpen={isSubscribeModalOpen}
                onClose={() => setIsSubscribeModalOpen(false)}
            />

        </div>
    );
};

export default DiscPage;